import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../utils/api';
import { latestOnly } from '../../utils/latestOnly';

// Single-invoice FULL email composer (owner 2026-08-26): no tiny recipients-only dialog — the
// operator sees the COMPLETE email (from / to / cc / subject / rendered body / PDF) and just checks
// it and hits Send. Two templates: the monthly fee request (TPL-019 service-fee wording) and the
// "Payment Request – [Description] – [Client]" (TPL-043) whose description defaults to the
// invoice's first line and can be switched from the invoice lines or typed. The default comes from
// the server (monthly_accounting: a line on a service flagged "Monthly accounting service", owner
// Q7 2026-10-03); the header toggle switches either way and reloads the draft.
type Extra = { filename: string; contentBase64: string; contentType?: string };
const validEmail = (e: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e);
const fileToDataUrl = (f: File) => new Promise<string>((resolve, reject) => {
  const r = new FileReader(); r.onload = () => resolve(String(r.result || '')); r.onerror = reject; r.readAsDataURL(f);
});

function EmailChips({ value, onChange, ph }: { value: string[]; onChange: (v: string[]) => void; ph: string }) {
  const [q, setQ] = useState('');
  const add = (raw: string) => { const v = raw.trim().toLowerCase(); if (v && validEmail(v) && !value.includes(v)) onChange([...value, v]); setQ(''); };
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center', padding: '6px 8px', border: '1px solid var(--border-strong)', borderRadius: 9, background: 'var(--c-gray-50)' }}>
      {value.map(e => (
        <span key={e} style={{ fontSize: 12, fontWeight: 600, padding: '3px 8px', borderRadius: 8, background: 'var(--accent-bg)', color: 'var(--accent-text)', display: 'inline-flex', alignItems: 'center', gap: 5 }}>
          {e}<span onClick={() => onChange(value.filter(x => x !== e))} style={{ cursor: 'pointer', fontWeight: 800 }}>×</span>
        </span>
      ))}
      <input value={q} onChange={e => setQ(e.target.value)} placeholder={ph}
        onKeyDown={e => { if ((e.key === 'Enter' || e.key === ',') && q.trim()) { e.preventDefault(); add(q); } }}
        onBlur={() => q.trim() && add(q)}
        style={{ flex: 1, minWidth: 140, border: 'none', outline: 'none', background: 'transparent', fontSize: 13, color: 'var(--text-primary)' }} />
    </div>
  );
}

export default function InvoiceEmailComposer({ invoiceId, defaultName, onClose }: { invoiceId: string; defaultName?: string; onClose: () => void }) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [isMonthly, setIsMonthly] = useState(true);
  const [defaultMonthly, setDefaultMonthly] = useState(true);   // what the service catalogue says
  const [defaultDesc, setDefaultDesc] = useState('');           // the payment request's default description
  const [switching, setSwitching] = useState(false);
  const [descOpts, setDescOpts] = useState<string[]>([]);
  const [desc, setDesc] = useState('');
  const [descCustom, setDescCustom] = useState(false);
  const [docNo, setDocNo] = useState('');
  const [clientName, setClientName] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState<string[]>([]);
  const [cc, setCc] = useState<string[]>([]);
  const [subject, setSubject] = useState('');
  const [initialBody, setInitialBody] = useState('');
  const [bodyKey, setBodyKey] = useState(0);            // remount the contentEditable on re-prepare
  const [attachmentName, setAttachmentName] = useState('');
  const [includePdf, setIncludePdf] = useState(true);
  const [extras, setExtras] = useState<Extra[]>([]);
  const [warning, setWarning] = useState('');
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const bodyRef = useRef<HTMLDivElement | null>(null);
  const latestDraft = useRef(latestOnly()).current;     // only the latest draft request may fill the form

  // Draft loads can overlap (a new description, then a template switch): every reply but the one of
  // the LATEST request is dropped, its error too. Returns false when this reply was dropped.
  const loadDraft = async (monthly: boolean, description: string): Promise<boolean> => {
    const r = await latestDraft(() => api.prepareInvoiceEmail(invoiceId, defaultName || undefined,
      monthly ? undefined : 'payment_request', monthly ? undefined : description));
    if (!r.latest) return false;
    const d: any = r.value;
    setFrom(d.from || ''); setTo(d.to || []); setCc(d.cc || []);
    setSubject(d.subject || ''); setInitialBody(d.bodyHtml || ''); setBodyKey(k => k + 1);
    setDocNo(d.docNo || ''); setClientName(d.clientName || ''); setAttachmentName(d.attachmentName || '');
    setWarning(d.warning || '');
    return true;
  };

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const d: any = await api.getInvoice(invoiceId);
        const names = Array.from(new Set(((d?.items || []) as any[]).filter((it: any) => !Number(it.is_note)).map((it: any) => String(it.name || '').trim()).filter(Boolean))) as string[];
        // Monthly bill ⇔ a line's service is flagged "Monthly accounting service" in the catalogue
        // (server: services/monthlyAccounting.ts; lines without a service keep the old name rule). The
        // period is NOT a usable signal — the manual invoice form defaults it to the full current month.
        const monthly = !!d?.monthly_accounting;
        // Subject descriptor default (owner 2026-08-26): a single-line invoice uses that line's
        // name; a multi-line invoice reads "<first line> and others" — the BODY details every line.
        const d0 = names.length === 1 ? names[0] : names.length > 1 ? `${names[0]} and others` : `Invoice ${String(d?.doc_no || '')}`.trim();
        if (!alive) return;
        setIsMonthly(monthly); setDefaultMonthly(monthly); setDefaultDesc(d0); setDescOpts(names); setDesc(monthly ? '' : d0);
        await loadDraft(monthly, d0);
      } catch (e: any) { if (alive) setErr(e?.message || 'Failed to prepare the email'); }
      finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, [invoiceId]); // eslint-disable-line

  // Two-way template switch: reloads the whole draft (as a new description does). The payment
  // request keeps its description logic — the last one chosen, else the invoice's default.
  const switchTemplate = async (monthly: boolean) => {
    if (monthly === isMonthly || switching || busy) return;
    const d = monthly ? '' : (desc.trim() || defaultDesc);
    setSwitching(true); setErr('');
    try {
      // Superseded by a later load (e.g. a description changed meanwhile) → that one owns the form.
      if (await loadDraft(monthly, d)) { setIsMonthly(monthly); setDescCustom(false); if (!monthly) setDesc(d); }
    } catch (e: any) { setErr(e?.message || 'Failed'); }
    finally { setSwitching(false); }
  };

  const changeDesc = async (v: string) => {
    setDesc(v); if (!v.trim()) return;
    setErr('');
    try { await loadDraft(false, v.trim()); } catch (e: any) { setErr(e?.message || 'Failed'); }
  };

  const onAddFiles = async (files: FileList | null) => {
    if (!files) return;
    const added: Extra[] = [];
    for (const f of Array.from(files)) added.push({ filename: f.name, contentBase64: await fileToDataUrl(f), contentType: f.type || undefined });
    setExtras(prev => [...prev, ...added]);
  };

  const send = async () => {
    if (!to.length) { setErr(t('iv_email_need')); return; }
    if (!isMonthly && !desc.trim()) { setErr(t('iv_payreq_need')); return; }
    setBusy(true); setErr('');
    try {
      const body = bodyRef.current ? bodyRef.current.innerHTML : initialBody;
      await api.sendInvoiceEmailCustom(invoiceId, { from, to, cc, subject, bodyHtml: body, includePdf, attachments: extras });
      setDone(true); setTimeout(onClose, 2600);
    } catch (e: any) { setErr(e?.message || 'Failed to send'); } finally { setBusy(false); }
  };

  const lbl: React.CSSProperties = { fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.03em', color: 'var(--text-secondary)', display: 'block', marginBottom: 5 };
  const inp: React.CSSProperties = { width: '100%', padding: '9px 11px', fontSize: 13, border: '1px solid var(--border-strong)', borderRadius: 9, background: 'var(--c-gray-50)', color: 'var(--text-primary)', outline: 'none', boxSizing: 'border-box' };
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 10003, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onMouseDown={e => e.target === e.currentTarget && !busy && onClose()}>
      <div style={{ width: 680, maxWidth: '94vw', maxHeight: '92vh', overflowY: 'auto', background: 'var(--bg-surface)', borderRadius: 14, padding: 22, boxShadow: 'var(--shadow-md)' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
          <div style={{ fontSize: 16, fontWeight: 800, color: 'var(--text-primary)' }}>{t('iv_email_title')}</div>
          <div role="radiogroup" aria-label={t('mf_tpl_label')} title={t('mf_tpl_switch_hint')}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 2, padding: 2, borderRadius: 999, border: '1px solid var(--border-strong)', background: 'var(--c-gray-50)', opacity: switching ? 0.7 : 1 }}>
            {([true, false] as const).map(m => {
              const on = isMonthly === m;
              const col = m ? { fg: '#0F766E', bg: '#ECFDF5', bd: '#99F6E4' } : { fg: '#B45309', bg: '#FFF7ED', bd: '#FBD38D' };
              return (
                <button key={String(m)} type="button" role="radio" aria-checked={on} disabled={loading || switching || busy || done}
                  onClick={() => void switchTemplate(m)}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 11.5, fontWeight: 700, borderRadius: 999, padding: '3px 10px', cursor: on ? 'default' : 'pointer',
                    color: on ? col.fg : 'var(--text-secondary)', background: on ? col.bg : 'transparent', border: `1px solid ${on ? col.bd : 'transparent'}` }}>
                  <i className={`ti ${m ? 'ti-calendar-repeat' : 'ti-file-dollar'}`} style={{ fontSize: 13 }} aria-hidden />
                  {m ? t('mf_tpl_monthly') : t('mf_tpl_payreq')}
                  {!loading && m === defaultMonthly && <span style={{ fontSize: 10, fontWeight: 600, opacity: 0.75 }}>· {t('mf_tpl_default')}</span>}
                </button>
              );
            })}
          </div>
        </div>
        {(docNo || clientName) && <div style={{ fontSize: 12.5, color: 'var(--text-tertiary)', marginTop: 4 }}>{docNo}{clientName ? ` · ${clientName}` : ''}</div>}

        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}><i className="ti ti-loader" style={{ fontSize: 24, color: 'var(--text-tertiary)' }} /></div>
        ) : (
          <div style={{ marginTop: 14 }}>
            {warning && <div style={{ fontSize: 12.5, color: '#92400E', background: '#FEF3C7', borderRadius: 9, padding: '8px 11px', marginBottom: 12 }}><i className="ti ti-alert-triangle" style={{ marginRight: 6 }} />{warning}</div>}

            {!isMonthly && (
              <>
                <label style={lbl}>{t('iv_payreq_desc')} <span style={{ color: '#DC2626' }}>*</span></label>
                {!descCustom ? (
                  <select value={descOpts.includes(desc) ? desc : ''} onChange={e => { if (e.target.value === '__custom__') { setDescCustom(true); } else void changeDesc(e.target.value); }} style={{ ...inp, marginBottom: 12 }}>
                    {!descOpts.includes(desc) && desc && <option value="">{desc}</option>}
                    {descOpts.map(o => <option key={o} value={o}>{o}</option>)}
                    <option value="__custom__">{t('iv_payreq_custom')}</option>
                  </select>
                ) : (
                  <div style={{ display: 'flex', gap: 6, marginBottom: 12 }}>
                    <input autoFocus value={desc} onChange={e => setDesc(e.target.value)} onBlur={() => desc.trim() && void changeDesc(desc)} maxLength={200}
                      onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); desc.trim() && void changeDesc(desc); } }}
                      placeholder={t('iv_payreq_custom_ph')} style={{ ...inp, flex: 1 }} />
                    <button type="button" onClick={() => setDescCustom(false)} title={t('cancel')}
                      style={{ border: '1px solid var(--border-strong)', background: 'var(--bg-app)', color: 'var(--text-secondary)', borderRadius: 9, padding: '0 12px', fontWeight: 700, cursor: 'pointer' }}>✕</button>
                  </div>
                )}
              </>
            )}

            <label style={lbl}>{t('bie_from')}</label>
            <input value={from} onChange={e => setFrom(e.target.value)} style={inp} />

            <label style={{ ...lbl, marginTop: 13 }}>{t('bie_to')}</label>
            <EmailChips value={to} onChange={setTo} ph={t('bie_email_ph')} />

            <label style={{ ...lbl, marginTop: 13 }}>{t('bie_cc')}</label>
            <EmailChips value={cc} onChange={setCc} ph={t('bie_email_ph')} />

            <label style={{ ...lbl, marginTop: 13 }}>{t('bie_subject')}</label>
            <input value={subject} onChange={e => setSubject(e.target.value)} style={inp} />

            <label style={{ ...lbl, marginTop: 13 }}>{t('bie_body')}</label>
            <div key={bodyKey} ref={bodyRef} contentEditable suppressContentEditableWarning
              dangerouslySetInnerHTML={{ __html: initialBody }}
              style={{ minHeight: 170, maxHeight: 300, overflowY: 'auto', padding: '11px 13px', fontSize: 13.5, lineHeight: 1.6, border: '1px solid var(--border-strong)', borderRadius: 9, background: 'var(--bg-surface)', color: 'var(--text-primary)', outline: 'none' }} />

            <label style={{ ...lbl, marginTop: 13 }}>{t('bie_attachment')}</label>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
              {attachmentName && (
                <label style={{ display: 'flex', alignItems: 'center', gap: 9, fontSize: 13, color: 'var(--text-primary)', cursor: 'pointer' }}>
                  <input type="checkbox" checked={includePdf} onChange={e => setIncludePdf(e.target.checked)} />
                  <i className="ti ti-file-type-pdf" style={{ color: '#E24B4A', fontSize: 16 }} />
                  <span style={{ fontWeight: 600 }}>{attachmentName}</span>
                  <span onClick={ev => { ev.preventDefault(); api.openInvoicePdf(invoiceId).catch(() => {}); }} style={{ color: 'var(--accent)', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>{t('bie_preview_pdf')}</span>
                </label>
              )}
              {extras.map((x, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 9, fontSize: 13, color: 'var(--text-primary)' }}>
                  <i className="ti ti-paperclip" style={{ fontSize: 15, color: 'var(--text-tertiary)' }} />
                  <span style={{ fontWeight: 600 }}>{x.filename}</span>
                  <span onClick={() => setExtras(prev => prev.filter((_, j) => j !== i))} style={{ cursor: 'pointer', color: '#E24B4A', fontWeight: 800, marginLeft: 2 }}>×</span>
                </div>
              ))}
              <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12.5, fontWeight: 600, color: 'var(--accent)', cursor: 'pointer', width: 'fit-content' }}>
                <i className="ti ti-plus" />{t('bie_add_file')}
                <input type="file" multiple style={{ display: 'none' }} onChange={e => { void onAddFiles(e.target.files); e.target.value = ''; }} />
              </label>
            </div>

            {err && <div style={{ fontSize: 12.5, color: '#E24B4A', background: '#FCEBEB', borderRadius: 9, padding: '8px 11px', marginTop: 13 }}><i className="ti ti-alert-circle" style={{ marginRight: 6 }} />{err}</div>}
          </div>
        )}

        {done ? <div style={{ fontSize: 13.5, color: '#B45309', fontWeight: 700, marginTop: 16 }}><i className="ti ti-mail-pause" style={{ marginRight: 6 }} />{t('oe_queued')}</div> : (
          <div style={{ display: 'flex', gap: 10, marginTop: 20, justifyContent: 'flex-end' }}>
            <button onClick={() => !busy && onClose()} style={{ padding: '9px 18px', borderRadius: 10, border: 'none', background: 'var(--c-gray-100)', color: 'var(--text-secondary)', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>{t('cancel')}</button>
            <button onClick={send} disabled={busy || loading} style={{ padding: '9px 24px', borderRadius: 10, border: 'none', background: 'var(--accent)', color: '#fff', fontWeight: 800, fontSize: 13.5, cursor: 'pointer', opacity: busy || loading ? 0.7 : 1 }}>
              {busy ? <><i className="ti ti-loader" style={{ marginRight: 6 }} />{t('bie_sending')}</> : <><i className="ti ti-send" style={{ marginRight: 6 }} />{t('iv_email_send')}</>}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
